'use client';

import { c as _c } from "react/compiler-runtime";
import { Picture } from '@petsmart-ui/sparky';
import * as Sentry from '@sentry/nextjs';
import { useEffect, useRef } from 'react';
import { NAVIGATION_URLS } from '@libs/core';
import { PICTURES } from '../../constants';
import { ErrorNotFound } from '../ErrorNotFound';
import { IMAGE_SIZES, pictureItems } from './constants';
import type { GlobalErrorProps } from './types';
import { is404 } from './utils';

// GlobalErrorBody is used as:
// 1. As a body of GlobalErrorPage, used in global-error.tsx in HCP, ACP, PLP, PDP, cart, checkout
// 2. error.tsx fallback component
// 3. Part of a static stand-alone error page.
export default function GlobalErrorBody(t0) {
  const $ = _c(16);
  const {
    error
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const loggedErrors = useRef(t1);
  let t2;
  let t3;
  if ($[1] !== error) {
    t2 = () => {
      if (error?.digest && !loggedErrors.current.includes(error.digest)) {
        loggedErrors.current.push(error.digest);
        Sentry.captureException(error, {
          extra: {
            ...error
          }
        });
      }
    };
    t3 = [error];
    $[1] = error;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  if (is404(error)) {
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <ErrorNotFound />;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    return t4;
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <style>{`
                    .body-container {
                        padding: 32px 16px;
                        width: 100%;
                        font: normal normal 200 16px/24px Euclid Circular B;
                        color: var(--sparky-color-neutral-900);
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        text-align: center;
                    }

                    .text-container {
                        display: flex;
                        flex-direction: column;
                        min-width: ${IMAGE_SIZES.SizeSm}px;
                        width: ${IMAGE_SIZES.SizeSm}px;
                    }

                    .text-container__header {
                        margin: 0;
                        font-size: 36px;
                        line-height: 44px;
                    }

                    .text-container__subtext {
                        margin-top: 16px;
                        font-weight: 600;
                        font-size: 18px;
                        line-height: 24px;
                    }

                    .body-container__image {
                        display: block;
                        margin: 0 auto;
                        margin-top: 16px;
                        min-width: ${IMAGE_SIZES.SizeSm}px;
                        width: ${IMAGE_SIZES.SizeSm}px;
                        height: ${IMAGE_SIZES.SizeSm}px;
                    }

                    .btn-container {
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        width: 100%;
                    }

                    .btn-container__button {
                        width: 100%;
                        min-width: ${IMAGE_SIZES.SizeSm}px;
                        height: 48px;
                        margin-top: 16px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        border-radius: 4px;
                        cursor: pointer;
                        background-color: var(--sparky-theme-color-primary-background);
                        color: #FFFFFF;
                        border: none;
                        padding: 14px 24px;
                        text-decoration: none;
                    }

                    .btn-container__button:hover {
                        background-color: var(--sparky-theme-color-primary-hover);
                    }

                    .btn-container__button:focus {
                        background-color: var(--sparky-theme-color-primary-focus);
                    }

                    @media (min-width: 560px) {
                        .body-container {
                            padding: 32px;
                        }

                        .text-container {
                            width: ${IMAGE_SIZES.SizeMd}px;
                        }
                        .body-container__image {
                            width: ${IMAGE_SIZES.SizeMd}px;
                            height: ${IMAGE_SIZES.SizeMd}px;
                        }

                        .text-container__subtext {
                            white-space: nowrap
                        }
                    }

                    @media (min-width: 960px) {
                        .body-container {
                            padding: 64px;
                        }

                        .text-container {
                            width: ${IMAGE_SIZES.SizeLg}px;
                        }

                        .text-container__header {
                            font-size: 40px;
                            line-height: 48px;
                        }

                        .text-container__subtext {
                            font-size: 20px;
                            line-height: 28px;
                        }

                        .body-container__image {
                            width: ${IMAGE_SIZES.SizeLg}px;
                            height: ${IMAGE_SIZES.SizeLg}px;
                        }

                        .btn-container__button {
                            width: auto;
                            min-width: auto;
                        }
                    }
                `}</style>;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <h1 className="text-container__header">Oops!</h1>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== error?.digest) {
    t6 = error?.digest ? <div>Error {error?.digest}</div> : null;
    $[7] = error?.digest;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div>Something went wrong. Please try again.</div>;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t6) {
    t8 = <div className="text-container">{t5}<div className="text-container__subtext" data-testid="global-error">{t6}{t7}</div></div>;
    $[10] = t6;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Picture className="body-container__image" imgSrc={`${PICTURES.NOT_FOUND.SRC}?fmt=webp&wid=250&hei=250`} items={pictureItems} imgAlt="Sad dog" data-testid="error-picture" />;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="btn-container"><a className="btn-container__button" aria-label="Reset button" data-testid="refresh-link" href={NAVIGATION_URLS.legacy.base}>Continue shopping</a></div>;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t8) {
    t11 = <div data-testid="error-body">{t4}<div className="body-container">{t8}{t9}{t10}</div></div>;
    $[14] = t8;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  return t11;
}